import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Container,
  InputAdornment,
  IconButton,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Divider,
  Select,
  MenuItem,
  Link
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Person, Phone, Check, Lock, Code, ArrowBackIos, ArrowForwardIos, Bedtime, WbSunny, WbTwilight, DoNotDisturb, Flare, Today, DarkMode, LightMode } from '@mui/icons-material'
import { signUpUser, authenticateUsers, confirmRegistrationUser, resendConfirmationCodeUsers } from '../../lib/authenticate';
import { putUser } from '../../lib/connector/userQueries';
import userpool from '../../lib/pools/userpool';
import { useToast } from '../../components/modals/ToastProvider/ToastProvider';
import logo from '../../static/logo_main_3124x512.png';
import logoDark from '../../static/logo_main_dark_3124x512.png';
import TermsAndConditions from '../../components/TermsAndConditions';
import PrivacyNotice from '../../components/PrivacyNotice';
import { DynamoDBClient, GetItemCommand } from "@aws-sdk/client-dynamodb";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { putDefaultAvailability } from '../../lib/connector/availabilityQueries';
import { useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import { getUserPool } from '../../lib/pools/pools';
import { putInviteRequest } from '../../lib/connector/requestQueries';
import moment from 'moment';

export const INVITE_EXPIRATION_HOURS = 24 * 7;

const EmployeeJoin: React.FC = () => {
  const navigate = useNavigate()
  const theme = useTheme();
  const toast = useToast();
  const { isDarkMode, toggleTheme } = useThemeContext();
  const [scheduleId, setScheduleId] = useState("")
  const [expirey, setExpirey] = useState("")
  const [resendLinkVisible, setResendLinkVisible] = useState<boolean>(false)
  const [step, setStep] = useState(0);
  const [availabilityStep, setAvailabilityStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
  });
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [inviteLoading, setInviteLoading] = useState<boolean>(true);
  const [errorMessageVisible, setErrorMessageVisible] = useState<boolean>(false);
  const [ownerId, setOwnerId] = useState<string>('');
  const [inviteError, setInviteError] = useState<string>('');
  const [scheduleInfo, setScheduleInfo] = useState<any>(null);
  const [verifyTimer, setVerifyTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [weekDayAvailability, setWeekDayAvailability] = useState<Record<string, any>>({
    Sunday: 'Unavailable',
    Monday: 'Unavailable',
    Tuesday: 'Unavailable',
    Wednesday: 'Unavailable',
    Thursday: 'Unavailable',
    Friday: 'Unavailable',
    Saturday: 'Unavailable',
  });
  
  const [submitting] = useState(false);

  useEffect(() => {
    setOwnerId(window.location.pathname.split("/")[2]);
    setScheduleId(window.location.pathname.split("/")[3]);
    setExpirey(window.location.pathname.split("/")[4])
  }, []);

  useEffect(() => {
    validateInviteLink();
  }, [scheduleId, expirey]);

  useEffect(() => {
    if (step === 2) {
      // Hide link initially and schedule to show after 5 minutes
      setResendLinkVisible(false);
      setVerifyTimer(setTimeout(() => {
        setResendLinkVisible(true);
      }, 5 * 60 * 1000)) // 5 minutes
    } else {
      // Ensure link hidden on other steps
      setResendLinkVisible(false);
    }
    return () => {
      if (verifyTimer) {
        clearTimeout(verifyTimer);
        setVerifyTimer(null);
      }
    };
  }, [step]);

  const validateInviteLink = async () => {
    setInviteLoading(true);
    setInviteError('');

    try {
      // Extract timestamp from invite token (format: invite-{timestamp})
      const tokenParts = expirey?.split('-');
      if (!tokenParts || tokenParts.length < 2) {
        setInviteError('Invalid invite link format');
        setInviteValid(false);
        setInviteLoading(false);
        return;
      }
      const inviteTimestamp = parseInt(tokenParts[1]);
      const currentTimestamp = Date.now();
      const expirationTime = INVITE_EXPIRATION_HOURS * 60 * 60 * 1000;

      const getSchedulePublic = async (id: string) => {
        const credentials = fromCognitoIdentityPool({
          identityPoolId: "us-east-1:381437dd-c20e-4c2e-b391-81ab6d137d7b",
          clientConfig: { region: "us-east-1" },
        });

        // Create a DynamoDB client with the temporary credentials
        const client = new DynamoDBClient({
          region: "us-east-1",
          credentials: credentials,
        });

        return await client.send(new GetItemCommand({
          TableName: "Team_Management_Schedules_Public",
          Key: {
            scheduleId: { S: scheduleId },
            userId: { S: ownerId }
          }
        })).then((data) => {
          if (data.Item) {
            return {
              scheduleId: data.Item.scheduleId.S,
              userId: data.Item.userId.S,
              name: data.Item.name.S,
              link: data.Item.link.S,
            };
          }
          return null;
        });
      };
      const schedule = await getSchedulePublic(scheduleId!);

      if (schedule) {
        // Check if invite is expired
        if (schedule.link !== window.location.href || currentTimestamp - inviteTimestamp > expirationTime) {
          setInviteError('Invalid invite. Please request a new invitation from your manager.');
          setInviteValid(false);
          setInviteLoading(false);
          return;
        }
        setScheduleInfo(schedule);
        setInviteValid(true);
        setStep(1); // Move to form
      } else {
        setInviteError('Invalid invite. Please request a new invitation from your manager.');
        setInviteValid(false);
      }
    } catch (error: any) {
      setInviteError(error.message || 'Error validating invite link. Please try again or contact your manager.');
      setInviteValid(false);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Minimal validation placeholder: returns empty errors object when valid
  const validate = (): { email?: string; password?: string } => {
    // Add real validation here if needed
    return {}
  }

  const passwordRules = useMemo(() => {
    const p = formData.password || '';
    return {
      length: p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      lowercase: /[a-z]/.test(p),
      number: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
    };
  }, [formData.password]);

  const passwordValid = Object.values(passwordRules).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = validate()
    if (Object.keys(errors).length > 0) {
      toast.showError('Please fix the validation errors before submitting.');
      return
    }

    if (step === 1) {
      try {
        await signUpUser(
          formData.fullName,
          formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""),
          formData.password,
          ownerId,
          scheduleId
        )
        setStep(2);
      }
      catch (err: any) {
        if (err.code === 'UsernameExistsException') {
          toast.showError('User already Exsts. Please reach out to support if you need help', 'Sign Up Failed');
          setErrorMessageVisible(true);
        } else if (err.code === 'LimitExceededException') {
          toast.showError('Attempt limit exceeded, please try again later.', 'Sign Up Failed');
        } else if (err.code === 'InvalidParameterException') {
          if (err.message.includes('password')) {
            toast.showError('Password does not meet complexity requirements. Please ensure it is at least 8 characters long and includes uppercase letters, lowercase letters, numbers, and special characters.', 'Sign Up Failed');
          }
        } else {
          toast.showError('An error occurred during sign up. Please reach out to support if you need help', 'Sign Up Failed');
        }
      }
    } else if (step === 2) {
      await confirmRegistrationUser(formData.confirmationCode, "+1" + formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", "")).then(async () => {
        try {
          await new Promise(resolve => setTimeout(resolve, 800));
          await authenticateUsers(formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""), formData.password).then(async () => {
            await new Promise(resolve => setTimeout(resolve, 800));
            const { v4: uuidv4 } = require('uuid');
            await putInviteRequest(scheduleId, uuidv4(), "JOIN", getUserPool()!.getCurrentUser()!.getUsername(),
              formData.fullName, moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"), JSON.stringify({
                userId: getUserPool()!.getCurrentUser()!.getUsername(),
                joinDate: moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
              }), 'pending')
            await putUser(getUserPool()!.getCurrentUser()!.getUsername(), "employee", formData.fullName, "", formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""), new Date().toISOString())
          })
          setStep(4);
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } catch (err: any) {
          toast.showError(err.message, 'Confirmation Failed');
        }
      })
    } else if (step === 3) {
      Object.keys(weekDayAvailability).forEach(async (day) => {
        let availability: Record<string, any> = {}
        if (weekDayAvailability[day] === 'Unavailable') {
        } else if (weekDayAvailability[day] === 'Morning (5am - 12pm)') {
          for (let i = 5; i <= 12; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
        } else if (weekDayAvailability[day] === 'Afternoon (12pm - 5pm)') {
          for (let i = 12; i <= 17; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
        } else if (weekDayAvailability[day] === 'Evening (5pm - 10pm)') {
          for (let i = 17; i <= 22; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
        } else if (weekDayAvailability[day] === 'Night (10pm - 5am)') {
          for (let i = 0; i <= 5; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
          for (let i = 22; i <= 23; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
        } else if (weekDayAvailability[day] === 'All Day (9am - 5pm)') {
          for (let i = 9; i <= 17; i++) {
            let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
            let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
            availability[i] = `${hourString}-${hourString2}`;
          }
        }
        await putDefaultAvailability(scheduleId, userpool.getCurrentUser()!.getUsername() + "#" + day.slice(0, 3), JSON.stringify(availability), userpool.getCurrentUser()!.getUsername(), day.slice(0, 3));
      });
      setStep(4);
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    }
  }

  // Loading state while validating
  if (inviteLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={6} sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>Validating invitation...</Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your invite link
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Error state if invite is invalid
  if (inviteValid === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={6} sx={{ borderRadius: 3, p: 4 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Invalid Invitation
              </Typography>
              {inviteError}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please contact your manager for a new invitation link.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => window.location.href = 'mailto:support@teamsync.com'}
            >
              Contact Support
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 50%, ${theme.palette.background.paper} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src={isDarkMode ? logoDark : logo}
            onClick={(() => {
              navigate('/')
            })}
            alt="Stafflows Logo"
            style={{
              width: "80%",
              cursor: 'pointer'
            }}
          />
        </Box>

        <Paper elevation={6} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Progress Stepper */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Stepper activeStep={step} alternativeLabel>
              <Step>
                <StepLabel>Verify Invite</StepLabel>
              </Step>
              <Step>
                <StepLabel>Your Information</StepLabel>
              </Step>
              <Step>
                <StepLabel>Verify Number</StepLabel>
              </Step>
              <Step>
                <StepLabel>Setup Availability</StepLabel>
              </Step>
              <Step>
                <StepLabel>Complete</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {/* Information */}
          {step === 1 && (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 4, pt: 2 }}>
              {!errorMessageVisible ? <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  You've been invited to join!
                </Typography>
                <Typography variant="body2">
                  <strong>{scheduleInfo?.name}</strong>
                </Typography>
              </Alert> : <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  User already exists with that phone number. Enter a new one or login at - <Link href="https://stafflows.com/login"
                    sx={{ color: 'inherit' }}>https://stafflows.com/login</Link>
                </Typography>
              </Alert>}



              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Welcome to the Team!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Let's get you started with just a few quick details
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Full Name */}
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Phone Number */}
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="(555) 123-4567"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="By joining, you agree to recieve SMS notifications for account verification purposes. Message and data rates may apply."
                />

                <Box aria-live="polite" sx={{ mt: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: passwordRules.length ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="caption" color={'text.primary'}>
                        At least 8 characters
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: passwordRules.uppercase ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="caption" color={'text.primary'}>
                        Uppercase letter (A-Z)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: passwordRules.lowercase ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="caption" color={'text.primary'}>
                        Lowercase letter (a-z)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: passwordRules.number ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="caption" color={'text.primary'}>
                        Number (0-9)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: passwordRules.special ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="caption" color={'text.primary'}>
                        Special character (e.g. !@#$%)
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Password */}
                <TextField
                  fullWidth
                  required
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="••••••••"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Confirm Password */}
                <TextField
                  fullWidth
                  required
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="••••••••"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText={formData.password === "" ? "" : formData.password === formData.confirmPassword ? "Passwords match." : "Passwords do not match."}
                />


                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || !formData.fullName || !formData.phoneNumber || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword || !passwordValid}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: 3,
                    mt: 2
                  }}
                >
                  {submitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      Creating your account...
                    </Box>
                  ) : (
                    'Join Team'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Confirmation */}
          {step === 2 && (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 4, pt: 2 }}>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Please Confirm your Account to Proceed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We messaged you a code, enter it below to confirm your account
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button variant='text' sx={{ fontWeight: 600 }} onClick={() => {
                    if (resendLinkVisible) {
                      resendConfirmationCodeUsers("+1" + formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""));
                    }
                    setResendLinkVisible(false);
                  }}>
                    {resendLinkVisible ? "Need a new code sent to " + formData.phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", "") + ", click here." : "Text sent. Please check your phone."}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Confirm Code */}
                <TextField
                  fullWidth
                  label="Confirmation Code"
                  name="confirmationCode"
                  value={formData.confirmationCode}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter your code"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Code color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || !formData.fullName || !formData.phoneNumber}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: 3,
                    mt: 2
                  }}
                >
                  {submitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      Creating your account...
                    </Box>
                  ) : (
                    'Join Team'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Availability Setup */}
          {step === 3 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Set Up Your Availability
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Let your manager know when you're generally available to work. You can update this anytime in your dashboard and adjust your availability
                before each schedule is published.
              </Typography>
              <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                {Object.keys(weekDayAvailability).map((weekDay, index) => (
                  <Box display={index === availabilityStep ? 'block' : 'none'} key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      {weekDay}s
                    </Typography>
                    <Divider sx={{ mb: 2 }} variant="middle" />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <IconButton
                        aria-label="previous day"
                        onClick={() => setAvailabilityStep((s) => Math.max(0, s - 1))}
                        disabled={index === 0}
                      >
                        <ArrowBackIos />
                      </IconButton>

                      <Box sx={{ flex: 1 }}>
                        {/* Middle UI content */}
                        {/* Controlled Select for availability */}
                        <Select
                          value={weekDayAvailability[weekDay] ?? 'Unavailable'}
                          onChange={(e) => {
                            const val = e.target.value as string;
                            setWeekDayAvailability((prev) => ({
                              ...prev,
                              [weekDay]: val,
                            }));
                          }}
                          renderValue={(value) => (
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 1, color: value === "Unavailable" ? `${theme.palette.error.light}`
                                : value === "Morning (5am - 12pm)" ? `${theme.palette.info.light}`
                                  : value === "Afternoon (12pm - 5pm)" ? `${theme.palette.warning.light}`
                                    : value === "Evening (5pm - 10pm)" ? `${theme.palette.primary.light}`
                                      : value === "Night (10pm - 5am)" ? `${theme.palette.secondary.light}`
                                        : `${theme.palette.primary.main}`
                            }}>
                              {value === 'Unavailable' ? <DoNotDisturb /> : value === 'Morning (5am - 12pm)' ? <Flare /> : value === 'Afternoon (12pm - 5pm)' ? <WbSunny /> : value === 'Evening (5pm - 10pm)' ? <WbTwilight /> : value === 'Night (10pm - 5am)' ? <Bedtime /> : <Today />}
                              <Typography variant="body2">{value}</Typography>
                            </Box>
                          )}
                          fullWidth
                        >
                          {['Unavailable', 'Morning (5am - 12pm)', 'Afternoon (12pm - 5pm)', 'Evening (5pm - 10pm)', 'Night (10pm - 5am)', 'All Day (9am - 5pm)'].map((option) => (
                            <MenuItem key={option} value={option} sx={{
                              color: option === "Unavailable" ? `${theme.palette.error.light}`
                                : option === "Morning (5am - 12pm)" ? `${theme.palette.info.light}`
                                  : option === "Afternoon (12pm - 5pm)" ? `${theme.palette.warning.light}`
                                    : option === "Evening (5pm - 10pm)" ? `${theme.palette.primary.light}`
                                      : option === "Night (10pm - 5am)" ? `${theme.palette.secondary.light}`
                                        : `${theme.palette.primary.main}`
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {option === 'Unavailable' ? <DoNotDisturb /> : option === 'Morning (5am - 12pm)' ? <Flare /> : option === 'Afternoon (12pm - 5pm)' ? <WbSunny /> : option === 'Evening (5pm - 10pm)' ? <WbTwilight /> : option === 'Night (10pm - 5am)' ? <Bedtime /> : <Today />}
                                <Typography variant="body2">{option}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>

                      <IconButton
                        aria-label="next day"
                        onClick={() => setAvailabilityStep((s) => Math.min(6, s + 1))}
                        disabled={index === 6}
                      >
                        <ArrowForwardIos />
                      </IconButton>
                    </Box>



                  </Box>
                ))}
              </Paper>

              {/* Availability legend: 7 colored boxes reflecting each day's selection */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                {Object.keys(weekDayAvailability).map((d, i) => {
                  const val = weekDayAvailability[d];
                  const color = val?.startsWith('Morning') ? theme.palette.info.main
                    : val?.startsWith('Afternoon') ? theme.palette.warning.main
                      : val?.startsWith('Evening') ? theme.palette.primary.main
                        : val?.startsWith('Night') ? theme.palette.secondary.main
                          : val === 'All Day (9am - 5pm)' ? theme.palette.success.main
                            : val === 'Unavailable' ? theme.palette.grey[400]
                              : theme.palette.grey[300];

                  return (
                    <Box
                      key={d}
                      onClick={() => setAvailabilityStep(i)}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.5,
                        backgroundColor: color,
                        border: availabilityStep === i ? `2px solid ${theme.palette.primary.dark}` : `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                      }}
                      title={d}
                    />
                  )
                })}
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{ mt: 3, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 600, boxShadow: 3 }}
              >
                Complete Setup
              </Button>

            </Box>
          )}

          {/* Success Step */}
          {step === 4 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <Check sx={{ fontSize: 48, color: 'success.dark' }} />
              </Box>

              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Welcome aboard, {formData.fullName}!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your account has been created successfully
              </Typography>

              <Alert severity="success" sx={{ mb: 3 }}>
                You're now part of <strong>{scheduleInfo?.name}</strong>
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Redirecting to your dashboard...
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard Now
              </Button>
            </Box>
          )}

        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
            <PrivacyNotice color={'primary'} />
            <Typography variant="caption" color="text.secondary">•</Typography>
            <TermsAndConditions color={'primary'} />
            <Typography variant="caption" color="text.secondary">•</Typography>
            <IconButton size='small' color={isDarkMode ? 'info' : 'primary'} onClick={toggleTheme}>
              {isDarkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default EmployeeJoin